import { gql } from "lib/apolloClient";
import { manageMembersFragments } from "AppComponents/DualPanelManageMembers/module/fragments";

export const getOrganizationGradesQuery = gql`
  query getOrganizationGrades(
    $organizationId: ID!
    $gradeFilters: GradeFilterInput
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        grades(filters: $gradeFilters) {
          id
          name
          staffCount
          studentCount
          familyMemberCount
          curriculumProgram @include(if: $getCurriculumProgram) {
            acronym
            label
            id
          }
        }
      }
    }
  }
`;

export const getGradeUsersQuery = gql`
  query getGradeUsers(
    $organizationId: ID!
    $filters: GradeFilterInput
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $allStudentsFilters: GradeAllStudentsFiltersInput
    $getOnlyUserId: Boolean!
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          curriculumProgram @include(if: $getCurriculumProgram) {
            id
            label
          }
          allStaffs @skip(if: $skipStaff) {
            id
            ...userDetailItem @skip(if: $getOnlyUserId)
          }
          allStudents(filters: $allStudentsFilters) @skip(if: $skipStudents) {
            id
            ...userDetailItem @skip(if: $getOnlyUserId)
          }
          allParents @skip(if: $skipFamilyMembers) {
            id
            ...userDetailItem @skip(if: $getOnlyUserId)
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getYearGroupUsersQuery = gql`
  query getYearGroupUsers(
    $organizationId: ID!
    $filters: YearGroupFilter
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $allStudentsFilters: YearGroupAllStudentsFiltersInput
    $getOnlyUserId: Boolean!
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          id
          name
          curriculumProgram @include(if: $getCurriculumProgram) {
            id
            label
          }
          allStaffs @skip(if: $skipStaff) {
            id
            ...userDetailItem @skip(if: $getOnlyUserId)
          }
          allStudents(filters: $allStudentsFilters) @skip(if: $skipStudents) {
            id
            ...userDetailItem @skip(if: $getOnlyUserId)
          }
          allParents @skip(if: $skipFamilyMembers) {
            id
            ...userDetailItem @skip(if: $getOnlyUserId)
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getStaffCourseUsersQuery = gql`
  query getCourseUsers(
    $id: ID!
    $first: Int
    $after: String
    $organizationId: ID!
    $filters: CourseFilter
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $studentFilters: CourseFilter
    $aclControlParameterFilter: ACLControlParameterFilter
    $getOnlyUserId: Boolean!
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $id, type: STAFF) {
      ... on Staff {
        id
        coursesV2(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                label
              }
              aclControlParameters(filters: $aclControlParameterFilter) {
                id
                uid
                permission
                value
              }
              students(filters: $studentFilters) @skip(if: $skipStudents) {
                edges {
                  node {
                    id
                    ...userDetailItem @skip(if: $getOnlyUserId)
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    id
                    ...userDetailItem @skip(if: $getOnlyUserId)
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    id
                    ...userDetailItem @skip(if: $getOnlyUserId)
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getOrganizationCourseUsersQuery = gql`
  query getCourseUsers(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: CourseFilterInput
    $skipStudents: Boolean!
    $skipStaff: Boolean!
    $skipFamilyMembers: Boolean!
    $studentFilters: CourseFilter
    $getOnlyUserId: Boolean!
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        courses(first: $first, after: $after, filters: $filters) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                label
              }
              students(filters: $studentFilters) @skip(if: $skipStudents) {
                edges {
                  node {
                    id
                    ...userDetailItem @skip(if: $getOnlyUserId)
                  }
                }
              }
              collaborator @skip(if: $skipStaff) {
                edges {
                  node {
                    id
                    ...userDetailItem @skip(if: $getOnlyUserId)
                  }
                }
              }
              familyMembers @skip(if: $skipFamilyMembers) {
                edges {
                  node {
                    id
                    ...userDetailItem @skip(if: $getOnlyUserId)
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;

export const getCalendarEventItemUsersPoolQuery = gql`
  query getCalendarEventItemUsersPool(
    $organizationId: ID!
    $getStudentFamilyMembers: Boolean!
    $getOnlyUserId: Boolean!
    $first: Int
    $after: String
    $before: String
    $orderBy: OrderByEnum
    $orderByDirection: ORDER_BY_DIRECTION
    $filters: CalendarEventItemUsersFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        calendarEventItemUsers(
          first: $first
          after: $after
          before: $before
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
        ) {
          pageInfo @skip(if: $getOnlyUserId) {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              ...userItem @skip(if: $getOnlyUserId)
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.userItem}
`;

export const getUserCountByEntityandEntityIds = gql`
  query userCountByEntityCourseWithEntityIds(
    $organizationId: ID!
    $input: UserCountByEntityInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        users {
          userCountByEntity(input: $input) {
            totalCount
            staffCount
            studentCount
            familyCount
          }
        }
      }
    }
  }
`;

// 2DTODO: Have to make fragment of this when queries are confirmed
export const getOrganizationCoursesQuery = gql`
  query getOrganizationCourses(
    $organizationId: ID!
    $courseFilters: CourseFilterInput
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(
          orderBy: TITLE
          orderByDirection: ASC
          filters: $courseFilters
        ) {
          edges {
            node {
              id
              title
              grades @include(if: $getGrades) {
                id
                name
                displaySequence
              }
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                type
                label
                acronym
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationCoursesUserCountQuery = gql`
  query getOrganizationCoursesUserCount($courseIds: [ID!]) {
    multiNode(ids: $courseIds, type: COURSE) {
      id
      ... on Course {
        students {
          totalCount
        }
        collaborator {
          totalCount
        }
        familyMembers {
          totalCount
        }
      }
    }
  }
`;

export const getUserCoursesV2Query = gql`
  query getUserCoursesV2(
    $id: ID!
    $filters: CourseFilter
    $userType: ENTITY_TYPE_ENUM!
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $aclControlParameterFilter: ACLControlParameterFilter
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        coursesV2(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          totalCount
          edges {
            node {
              id
              title
              grades @include(if: $getGrades) {
                id
                name
                displaySequence
              }
              curriculumProgram @include(if: $getCurriculumProgram) {
                id
                type
                label
                acronym
              }
              aclControlParameters(filters: $aclControlParameterFilter) {
                id
                uid
                permission
                value
              }
              students {
                totalCount
              }
              collaborator {
                totalCount
              }
              familyMembers {
                totalCount
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $id: ID!
    $filters: CourseFilter
    $courseGradeFilters: CourseGradeFilters
    $getGrades: Boolean!
    $getCurriculumProgram: Boolean!
    $userType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $userType) {
      id
      ... on Staff {
        id
        courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          id
          title
          grades(filters: $courseGradeFilters) @include(if: $getGrades) {
            id
            name
          }
          curriculumProgram @include(if: $getCurriculumProgram) {
            id
            type
            label
            acronym
          }
        }
      }
    }
  }
`;

export const getOperationalDaysQuery = gql`
  query getOperationalDaysQuery(
    $organizationId: ID!
    $filter: GlobalConstantsFilterOptions
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        globalConstants(filter: $filter) {
          name
          value
          id
        }
      }
    }
  }
`;

export const getLinkedThirdPartyAccountsQuery = gql`
  query getLinkedThirdPartyAccounts(
    $userId: ID!
    $userType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $userId, type: $userType) {
      ... on Staff {
        id
        linkedThirdPartyAccounts {
          id
          serviceType
          isConnected
          userName
          accessToken
        }
      }
    }
  }
`;

export const getCalendarUsersByIdsQuery = gql`
  query getCalendarUsersByIds(
    $filters: CalendarEventItemUsersFilters
    $organizationId: ID!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        calendarEventItemUsers(filters: $filters) {
          edges {
            node {
              ...userDetailItem
            }
          }
        }
      }
    }
  }
  ${manageMembersFragments.basicUserItem}
`;
